import React from 'react'
import PropTypes from 'prop-types'
import { PanelGroup, Panel } from 'react-bootstrap'
import { Checkbox } from 'react-icheck'
import uuid from 'uuid'

/* eslint no-template-curly-in-string: 0 */

// Content types a comment must include to match a tier (AND): text (a caption
// within the length bounds), image (image or gif), video. Tiers resolve in
// order, first match wins, so the most specific tier goes first.
const MEDIA_OPTIONS = [
  { key: 'text', label: 'Text' },
  { key: 'image', label: 'Image or GIF' },
  { key: 'video', label: 'Video' }
]

export const makeTier = count => ({
  id: uuid.v4(),
  name: `Tier ${count + 1}`,
  minLength: '',
  maxLength: '',
  media: ['text'],
  points: '',
  useRandom: false,
  response: '',
  randomResponses: ['']
})

// Store numeric fields as numbers, not the raw string: the evaluator reads
// tier length bounds and points with Number.isFinite, which is false for a
// string, so a saved '60' would make the band ignored. Empty stays '' so a
// blank field is distinguishable from 0.
const toNumberOrBlank = v => (String(v).trim() === '' ? '' : Number(v))

const numberInput = (value, onChange, placeholder) => (
  <input
    type='number'
    min={0}
    className='form-control'
    placeholder={placeholder}
    value={value == null ? '' : String(value)}
    onChange={e => onChange(toNumberOrBlank(e.target.value))}
  />
)

/**
 * Pulse Intelligence: Tiered Responses & Points card. Grade matched comments by
 * effort (length band + required media) and reply per tier; the first tier a
 * comment satisfies wins and fills ${tierResponse}. Points are optional; when on
 * each tier awards points and campaign-level bonuses (fast-fingers, tag-a-friend)
 * become available. Persists to widget_data.intentBased.{tierResponses,
 * tierPointsEnabled, pointBonuses} via redux-form change.
 */
const TierResponsesCard = ({
  tiers,
  pointsEnabled,
  pointBonuses,
  repeatResponse,
  change
}) => {
  const repeatEnabled = repeatResponse.enabled === true
  const updateRepeat = patch =>
    change('intentBased.repeatResponse', { ...repeatResponse, ...patch })
  const writeTiers = next => change('intentBased.tierResponses', next)
  const updateTier = (index, updates) =>
    writeTiers(
      tiers.map((tier, i) => (i === index ? { ...tier, ...updates } : tier))
    )
  const addTier = () => writeTiers([...tiers, makeTier(tiers.length)])
  const removeTier = index => writeTiers(tiers.filter((_, i) => i !== index))
  const moveTier = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= tiers.length) return
    const next = tiers.slice()
    const moved = next[index]
    next[index] = next[target]
    next[target] = moved
    writeTiers(next)
  }
  const toggleMedia = (index, key) => {
    const { media } = tiers[index]
    updateTier(index, {
      media: media.includes(key)
        ? media.filter(m => m !== key)
        : [...media, key]
    })
  }
  const updateVariant = (index, variantIndex, value) =>
    updateTier(index, {
      randomResponses: tiers[index].randomResponses.map((v, i) =>
        i === variantIndex ? value : v
      )
    })
  const addVariant = index =>
    updateTier(index, {
      randomResponses: [...tiers[index].randomResponses, '']
    })
  const removeVariant = (index, variantIndex) =>
    updateTier(index, {
      randomResponses: tiers[index].randomResponses.filter(
        (_, i) => i !== variantIndex
      )
    })

  const updateBonus = updates =>
    change('intentBased.pointBonuses', { ...pointBonuses, ...updates })

  return (
    <div className='form-group darkPanel' data-testid='tier-responses'>
      <div className='col-md-12'>
        <div className='portlet light'>
          <div className='portlet-title'>
            <div className='caption'>
              <span className='caption-subject bold font-green-haze uppercase'>
                <i
                  className='glyphicon glyphicon-list hidden-xs'
                  style={{ marginRight: 8 }}
                />
                Pulse Intelligence: Tiered Responses &amp; Points
              </span>
            </div>
          </div>
          <div className='portlet-body'>
            <div className='form-group row'>
              <div className='col-sm-12'>
                <p className='help-block'>
                  Grade matched comments by effort (length and required content)
                  and reply per tier. Tiers are checked top to bottom; the first
                  match wins and fills ${'{tierResponse}'} in a reply action.
                </p>
              </div>
            </div>
            <div className='form-group row'>
              <div className='col-sm-12'>
                <Checkbox
                  id='brt-pulse-points-enabled'
                  checkboxClass='icheckbox_square'
                  checked={pointsEnabled}
                  onChange={e =>
                    change('intentBased.tierPointsEnabled', e.target.checked)
                  }
                  label='Enable points'
                />
              </div>
            </div>

            <PanelGroup accordion id='pulse-tiers'>
              {tiers.map((tier, index) => (
                <Panel
                  eventKey={index}
                  key={tier.id}
                  data-testid={`tier-${index}`}
                >
                  <Panel.Heading className='flex-container expanded-panel'>
                    <Panel.Toggle componentClass='a' style={{ flexGrow: '0' }}>
                      <i className='fa fa-chevron-down' />
                    </Panel.Toggle>
                    <Panel.Title style={{ flexGrow: '8', marginLeft: '15px' }}>
                      <input
                        type='text'
                        className='br-input-list-name'
                        aria-label={`tier-${index}-name`}
                        value={tier.name || ''}
                        onChange={e =>
                          updateTier(index, { name: e.target.value })
                        }
                        onClick={e => e.stopPropagation()}
                      />
                    </Panel.Title>
                    <Panel.Title style={{ flexGrow: '0' }}>
                      <button
                        type='button'
                        className='btn btn-sm default'
                        aria-label={`tier-${index}-move-up`}
                        disabled={index === 0}
                        onClick={e => {
                          moveTier(index, -1)
                          e.stopPropagation()
                        }}
                      >
                        &uarr;
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm default'
                        aria-label={`tier-${index}-move-down`}
                        disabled={index === tiers.length - 1}
                        onClick={e => {
                          moveTier(index, 1)
                          e.stopPropagation()
                        }}
                      >
                        &darr;
                      </button>
                      <button
                        type='button'
                        className='btn btn-sm red'
                        aria-label={`tier-${index}-remove`}
                        onClick={e => {
                          removeTier(index)
                          e.stopPropagation()
                        }}
                      >
                        Remove
                      </button>
                    </Panel.Title>
                  </Panel.Heading>
                  <Panel.Body collapsible>
                    <div className='form-group row'>
                      <label className='col-sm-3 col-md-2 control-label'>
                        Length (chars)
                      </label>
                      <div className='col-sm-3 col-md-3'>
                        {numberInput(
                          tier.minLength,
                          v => updateTier(index, { minLength: v }),
                          'Min'
                        )}
                      </div>
                      <div className='col-sm-3 col-md-3'>
                        {numberInput(
                          tier.maxLength,
                          v => updateTier(index, { maxLength: v }),
                          'Max'
                        )}
                      </div>
                    </div>

                    <div className='form-group row'>
                      <label className='col-sm-3 col-md-2 control-label'>
                        Must include
                      </label>
                      <div className='col-sm-9 col-md-10'>
                        {MEDIA_OPTIONS.map(option => (
                          <span
                            key={option.key}
                            style={{ marginRight: 24, display: 'inline-block' }}
                          >
                            <Checkbox
                              checkboxClass='icheckbox_square'
                              checked={tier.media.includes(option.key)}
                              onChange={() => toggleMedia(index, option.key)}
                              label={option.label}
                            />
                          </span>
                        ))}
                      </div>
                    </div>

                    {pointsEnabled && (
                      <div className='form-group row'>
                        <label className='col-sm-3 col-md-2 control-label'>
                          Points (XP)
                        </label>
                        <div className='col-sm-3 col-md-3'>
                          {numberInput(
                            tier.points,
                            v => updateTier(index, { points: v }),
                            'Points'
                          )}
                        </div>
                      </div>
                    )}

                    <div className='form-group row'>
                      <div className='col-sm-12'>
                        <Checkbox
                          checkboxClass='icheckbox_square'
                          checked={tier.useRandom}
                          onChange={e =>
                            updateTier(index, { useRandom: e.target.checked })
                          }
                          label='Use random responses'
                        />
                      </div>
                    </div>

                    {tier.useRandom ? (
                      <div className='form-group row'>
                        <div className='col-sm-12'>
                          {tier.randomResponses.map((variant, variantIndex) => (
                            <div
                              key={variantIndex}
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'flex-start',
                                marginBottom: 8
                              }}
                            >
                              <textarea
                                className='form-control'
                                rows={2}
                                aria-label={`tier-${index}-variant-${variantIndex}`}
                                value={variant || ''}
                                onChange={e =>
                                  updateVariant(
                                    index,
                                    variantIndex,
                                    e.target.value
                                  )
                                }
                              />
                              <button
                                type='button'
                                className='btn btn-sm red'
                                aria-label={`tier-${index}-variant-${variantIndex}-remove`}
                                disabled={tier.randomResponses.length === 1}
                                onClick={() =>
                                  removeVariant(index, variantIndex)
                                }
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                          <button
                            type='button'
                            className='btn btn-sm green-haze'
                            aria-label={`tier-${index}-add-variant`}
                            onClick={() => addVariant(index)}
                          >
                            + Add variant
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className='form-group row'>
                        <div className='col-sm-12'>
                          <textarea
                            className='form-control'
                            rows={2}
                            aria-label={`tier-${index}-response`}
                            placeholder='Response text'
                            value={tier.response || ''}
                            onChange={e =>
                              updateTier(index, { response: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    )}
                  </Panel.Body>
                </Panel>
              ))}
            </PanelGroup>

            <button
              type='button'
              id='brt-pulse-tier-add'
              className='btn btn-sm green-haze'
              onClick={addTier}
            >
              + Add tier
            </button>

            {pointsEnabled && (
              <div data-testid='bonus-points' style={{ marginTop: 20 }}>
                <div className='form-group row'>
                  <div className='col-sm-12'>
                    <h5 className='bold uppercase font-green-haze'>
                      Bonus points
                    </h5>
                    <p className='help-block'>
                      Optional. Award extra points to the first responders
                      (fast-fingers) and to comments that tag a friend. Leave a
                      field blank to skip that bonus.
                    </p>
                  </div>
                </div>
                <div className='form-group row'>
                  <label className='col-sm-4 col-md-3 control-label'>
                    Fast-fingers: first N responders
                  </label>
                  <div className='col-sm-4 col-md-3'>
                    {numberInput(
                      pointBonuses.fastFingersCount,
                      v => updateBonus({ fastFingersCount: v }),
                      'e.g. 100'
                    )}
                  </div>
                </div>
                <div className='form-group row'>
                  <label className='col-sm-4 col-md-3 control-label'>
                    Fast-fingers bonus points
                  </label>
                  <div className='col-sm-4 col-md-3'>
                    {numberInput(
                      pointBonuses.fastFingersPoints,
                      v => updateBonus({ fastFingersPoints: v }),
                      'points'
                    )}
                  </div>
                </div>
                <div className='form-group row'>
                  <label className='col-sm-4 col-md-3 control-label'>
                    Tag-a-friend bonus points
                  </label>
                  <div className='col-sm-4 col-md-3'>
                    {numberInput(
                      pointBonuses.tagFriendPoints,
                      v => updateBonus({ tagFriendPoints: v }),
                      'points'
                    )}
                  </div>
                </div>
              </div>
            )}

            {pointsEnabled && (
              <div data-testid='repeat-engagement' style={{ marginTop: 20 }}>
                <div className='form-group row'>
                  <div className='col-sm-12'>
                    <h5 className='bold uppercase font-green-haze'>
                      Repeat engagement
                    </h5>
                    <p className='help-block'>
                      When on, a user's genuine repeat comment on a post gets
                      this reply instead of the tier response; when off, no
                      reply is sent on a repeat. Does not support $
                      {'{tierResponse}'} (first-class only); ${'{totalPoints}'},
                      ${'{greeting}'} and ${'{senderName}'} are fine.
                    </p>
                  </div>
                </div>
                <div className='form-group row'>
                  <div className='col-sm-12'>
                    <Checkbox
                      id='brt-pulse-repeat-enabled'
                      checkboxClass='icheckbox_square'
                      checked={repeatEnabled}
                      onChange={e =>
                        updateRepeat({ enabled: e.target.checked })
                      }
                      label='Reply on a repeat engagement'
                    />
                  </div>
                </div>
                {repeatEnabled && (
                  <div className='form-group row'>
                    <div className='col-sm-12'>
                      <textarea
                        className='form-control'
                        rows={3}
                        aria-label='repeat-response'
                        placeholder='Repeat reply (supports ${senderName}, ${greeting}, ${totalPoints})'
                        value={repeatResponse.response || ''}
                        onChange={e =>
                          updateRepeat({ response: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

TierResponsesCard.propTypes = {
  tiers: PropTypes.array,
  pointsEnabled: PropTypes.bool,
  pointBonuses: PropTypes.object,
  repeatResponse: PropTypes.object,
  change: PropTypes.func.isRequired
}

TierResponsesCard.defaultProps = {
  tiers: [],
  pointsEnabled: false,
  pointBonuses: {},
  repeatResponse: {}
}

export default TierResponsesCard



// WEBPACK FOOTER //
// ./src/pages/widgets/form/configure-pulse-intelligence/tier-responses-card.js